import * as React from 'react';
import { Box, Button, Paper, Popper, Grow, Stack, MenuItem, MenuList, ClickAwayListener } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { deleteGift } from 'src/shared/services/giftService';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GiftDetailMarkAsHandledDialog } from './giftDetailMarkAsHandledDialog';

type GiftDetailMarkAsHandledButtonProps = {
  depositId: number;
  disabled: boolean;
  isHandled: boolean;
};

const GiftDetailMarkAsHandledButton = (props: GiftDetailMarkAsHandledButtonProps) => {
  const { disabled, depositId, isHandled } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();
  const { setDateStamp } = useGlobals();
  const navigate = useNavigate();

  const [showDialog, setShowDialog] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState<boolean>(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleCancelCallback = () => {
    setShowDialog(false);
  };

  const onMarkAsHandled = () => {
    setOpen(false);
    setShowDialog(true);
  };

  const onHandleDeleteGift = async (id: number) => {
    try {
      await deleteGift(id);

      if (isMounted()) {
        toast.success(<>{t('gift:deleteGiftSuccess')}</>);
        setDateStamp(new Date());
        navigate(`${RoutesEnum.GiftsDetail}`);
      }
    } catch (e) {
      toast.error(<>{t('gift.markAsHandledError')}</>);
    }
  };

  return (
    <>
      <GiftDetailMarkAsHandledDialog depositId={depositId} showDialog={showDialog} callback={handleCancelCallback} />
      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={handleToggle}
            variant='contained'
            disabled={disabled}
            disableElevation
            endIcon={<KeyboardArrowDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper open={open} anchorEl={anchorRef.current} placement='bottom-start' transition disablePortal sx={{ zIndex: 999 }}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'right top' : 'right bottom',
                }}
              >
                <Paper elevation={10}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id='composition-menu' aria-labelledby='composition-button'>
                      <MenuItem onClick={onMarkAsHandled} disabled={isHandled}>
                        {t('gift:markAsHandled')}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setOpen(false);
                          setOpenDeleteDialog(true);
                        }}
                      >
                        {t('gift:deleteGift')}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value={depositId.toString()}
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            const id = Number(value.id);
            onHandleDeleteGift(id);
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};

export default GiftDetailMarkAsHandledButton;
